import { VariableValue } from '@components/text-editors/addons/field-mapping';
import { toQueryString } from '@utils/requests';

import { SetPassword, ResetPassword, UpdatePassword, SignUpUserData } from './hooks';

import { request } from '../request';

export type User = {
  id: string;
  shortId: string;
  name: string;
  email: string;
  phone?: string;
  role: 'user' | 'admin';
  picture: 'string';
  hasSetPassword: boolean;
  isMe: boolean;
  status: 'joined' | 'invited' | 'created';
  inviteDate?: string;
  lastLogin?: string;
};

export type CreatedBy = Pick<User, 'name' | 'picture'>;

export const loadUsers = async (queryString?: string) => {
  const response = await request.get<User[]>(`v1/users/${queryString ?? ''}`);
  return response.data;
};

export const createUser = async (data: FormData) => {
  const response = await request.post('v1/users/', data);
  return response.data;
};

export const editUser = async (id: string, data: FormData) => {
  const response = await request.patch(`v1/users/${id}/`, data);
  return response.data;
};

export const deleteUser = async (id: string) => {
  const response = await request.delete(`v1/users/${id}/`);
  return response.data;
};

export const canSetPassword = async (uuid: string, passwordUuid: string) => {
  const query = toQueryString({
    uuid,
    password_uuid: passwordUuid,
  });

  const response = await request.get(`v1/users/password/set${query}`);
  return response.data;
};

export const setPassword = async (data: SetPassword) => {
  const response = await request.patch('v1/users/password/set/', data);
  return response.data;
};

export const signUpUser = async (data: SignUpUserData) => {
  const response = await request.patch('v1/users/signup/', data);
  return response.data;
};

type SignupCompanyData = {
  businessEmail: string;
};

export const signupCompany = async (data: SignupCompanyData) => {
  const response = await request.post('v1/signup/', data);
  return response.data;
};

export const resendSignupConfirmation = async (data: SignupCompanyData) => {
  const response = await request.post('v1/signup/resend/', data);
  return response.data;
};

export type SetCompanyUserInfoData = {
  name: string;
  password: string;
};

export const setCompanyUserInfo = async (data: SetCompanyUserInfoData) => {
  const response = await request.post('v1/signup/user-info/', data);
  return response.data;
};

type SignupCompanyConfirmData = SignupCompanyData & {
  confirmationCode: string;
};

export const signupCompanyConfirm = async (data: SignupCompanyConfirmData) => {
  const response = await request.post('v1/signup/confirm/', data);
  return response.data;
};

export const signupCompanyConfirmUuid = async (uuid: string) => {
  const response = await request.get(`v1/signup/confirm?confirmation_uuid=${uuid}`);
  return response.data;
};

export const inviteUser = async (id: string) => {
  const response = await request.post(`v1/users/${id}/invite/`);
  return response.data;
};

export type UserInvite = {
  emails: VariableValue;
};

export const createAndInviteUsers = async (data: UserInvite) => {
  const response = await request.post('v1/users/invites/', data);
  return response.data;
};

type MyUser = {
  id: string;
  shortId: string;
  name: string;
  email: string;
  phone: string;
  picture: 'string';
  status: 'joined' | 'created' | 'invited';
  dateCreated: Date;
};

export const loadMyDetails = async () => {
  const response = await request.get<MyUser>('v1/users/me/');
  return response.data;
};

export const editMyDetails = async (data: FormData) => {
  const response = await request.patch('v1/users/me/', data);
  return response.data;
};

export const resetPassword = async (data: ResetPassword) => {
  const response = await request.post(`v1/users/password/reset/`, data);
  return response.data;
};

export const updatePassword = async (data: UpdatePassword) => {
  const response = await request.patch('v1/users/password/update/', data);
  return response.data;
};
